const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * True only when this build was given `VITE_TURNSTILE_SITE_KEY`. When false no
 * script is loaded, no widget renders, and no token is sent — the request is
 * byte-for-byte what it was before Turnstile existed. Pair it with the
 * `TURNSTILE_SECRET` on the Function: setting one without the other either
 * rejects every request (secret only) or shows a widget nobody checks (key only).
 */
export const turnstileEnabled = SITE_KEY !== '';

export interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': (code: string) => void;
  'expired-callback': () => void;
  theme?: 'auto' | 'light' | 'dark';
}

interface TurnstileApi {
  render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise !== null) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('The verification widget failed to load.')));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

let activeWidgetId: string | null = null;

/**
 * Loads Turnstile and renders a widget into `host`, resolving with its id.
 * Rejects if the script or the widget API never arrives.
 */
export async function renderTurnstile(
  host: HTMLElement,
  options: Omit<TurnstileRenderOptions, 'sitekey'>,
): Promise<string> {
  await loadScript();
  const api = window.turnstile;
  if (api === undefined) throw new Error('The verification widget loaded but exposed no API.');
  const widgetId = api.render(host, { sitekey: SITE_KEY, ...options });
  activeWidgetId = widgetId;
  return widgetId;
}

export function removeTurnstile(widgetId: string): void {
  window.turnstile?.remove(widgetId);
  if (activeWidgetId === widgetId) activeWidgetId = null;
}

/** Turnstile tokens are single-use; call this after every submitted request. */
export function resetTurnstile(): void {
  if (activeWidgetId !== null) window.turnstile?.reset(activeWidgetId);
}
